import { FC, ReactNode, createContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import useGLPeriodApi, { GLPeriods } from 'src/services/useGLPeriodApi';
import usePoDetailsApi from 'src/services/usePoDetailsApi';
import usePoHistoryApi, { PoHistory } from 'src/services/usePoHistoryApi';
import useVarianceLineItemApi from 'src/services/useVarianceLineItemApi';
import {
  IVarianceDetails,
  IVarianceGLCode,
  IVarianceGLPeriod,
  IVarianceLineItem,
} from 'src/types/VarianceDetails';
import { SetState } from 'src/types/generic';

interface IVarianceDetailsContext {
  poNumber: string;
  varianceId: string;
  poId: string;
  variances: IVarianceDetails;
  setVariances: SetState<IVarianceDetails>;
  getVariances: (params?: undefined) => Promise<IVarianceDetails>;
  isVarianceLoading: boolean;
  poHistory: PoHistory;
  isPoHistoryLoading: boolean;
  varianceLineItems: IVarianceLineItem[];
  setVarianceLineItems: SetState<IVarianceLineItem[]>;
  varianceGLPeriods: IVarianceGLPeriod[];
  setVarianceGLPeriods: SetState<IVarianceGLPeriod[]>;
  varianceGLCodes: IVarianceGLCode[];
  setVarianceGLCodes: SetState<IVarianceGLCode[]>;
  glPeriods: GLPeriods;
  isMultiplePeriodAssigned: boolean;
}

export const VarianceDetailsContext = createContext<IVarianceDetailsContext>(
  {} as IVarianceDetailsContext
);

const VarianceDetailsContextProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const poId = useMemo(() => searchParams.get('poId') || '', [searchParams]);
  const varianceId = useMemo(() => searchParams.get('varianceId') || '', [searchParams]);
  const {
    variances,
    setVariances,
    varianceLineItems,
    setVarianceLineItems,
    varianceGLCodes,
    setVarianceGLCodes,
    varianceGLPeriods,
    setVarianceGLPeriods,
    isVarianceLoading,
    getVariances,
  } = useVarianceLineItemApi(varianceId);
  const { poHistory, isLoading: isPoHistoryLoading } = usePoHistoryApi(poId, false);
  const poNumber = variances.poNum;

  const { poDetails } = usePoDetailsApi(poId);

  const isMultiplePeriodAssigned = poDetails?.allocations
    ? poDetails.allocations.length > 0
    : false;

  const { glPeriods } = useGLPeriodApi(String(poDetails?.companyId));

  return (
    <VarianceDetailsContext.Provider
      value={{
        poNumber,
        varianceId,
        varianceLineItems,
        setVarianceLineItems,
        poId,
        variances,
        setVariances,
        isVarianceLoading,
        getVariances,
        poHistory,
        isPoHistoryLoading,
        glPeriods,
        varianceGLPeriods,
        setVarianceGLPeriods,
        varianceGLCodes,
        setVarianceGLCodes,
        isMultiplePeriodAssigned,
      }}
    >
      {children}
    </VarianceDetailsContext.Provider>
  );
};

export default VarianceDetailsContextProvider;
